import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { IMemberSalesOrderInfoResult, ITourPrice } from '../utils/types';
import {
  getTourPricesOptions,
  updateTourTransferSalesIndividualMaterial,
} from '../api';
import { format } from 'date-fns';
import { AuthContext } from '../context/AuthContext';

export interface TransferTourDialogProps {
  open: boolean;
  onClose: () => void;
  data?: IMemberSalesOrderInfoResult;
}

export default function TransferTourDialog({
  open,
  onClose,
  data,
}: TransferTourDialogProps) {
  const { t } = useTranslation();
  const showSnackbar = useAlertSnackbar();
  const { token } = useContext(AuthContext);

  const [step, setStep] = useState<1 | 2>(1);
  const [newTourCode, setNewTourCode] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [transferError, setTransferError] = useState<string>('');
  const [tourPriceOptions, setTourPriceOptions] = useState<ITourPrice[]>([]);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [priceNotFoundError, setPriceNotFoundError] = useState<string | null>(
    null,
  );

  const today = new Date();

  useEffect(() => {
    if (open) {
      setStep(1);
      setNewTourCode('');
      setTransferError('');
    }
  }, [open]);

  if (!data) return null;

  const handleNext = () => {
    setTransferError('');
    setStep(2);
  };

  const fetchTourPrices = async () => {
    setTourPriceOptions([]);
    try {
      const res = await getTourPricesOptions(
        data.salesIndividualId,
        token,
        newTourCode.trim(),
      );
      if (!res.ok) throw new Error(res.statusText);

      const json = await res.json().catch(() => []);

      const filtered = Array.isArray(json)
        ? json.filter(
            (price: ITourPrice) => price.price.bedType.id === data.bedTypeId,
          )
        : [];

      setTourPriceOptions(filtered);
      if (tourPriceOptions.length < 1) {
        setPriceNotFoundError(t('orderDetailPage.transferTourDialog.closed'));
      }
    } catch (err: any) {
      showSnackbar(t('orderDetailPage.changeTour.fetchError'), 'error');
      setPriceError(t('orderDetailPage.transferTourDialog.priceError'));
    }
  };

  const handleCloseDialog = () => {
    setTourPriceOptions([]);
    setPriceError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!tourPriceOptions) {
      showSnackbar(t('transferTourDialog.selectPriceError'), 'error');
      return;
    }

    setSubmitting(true);
    setTransferError('');

    try {
      const res = await updateTourTransferSalesIndividualMaterial(
        data.salesIndividualId,
        tourPriceOptions[0]?.tour?.id,
        token,
      );

      if (res.ok) {
        showSnackbar(
          t('orderDetailPage.transferTourDialog.success'),
          'success',
        );
        handleCloseDialog();
      } else {
        setTransferError(t('orderDetailPage.transferTourDialog.closed'));
      }
    } catch (err: any) {
      showSnackbar(
        err.message || t('transferTourDialog.error.generic'),
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        {t(
          step === 1
            ? 'orderDetailPage.transferTourDialog.titleInfo'
            : 'orderDetailPage.transferTourDialog.titleTransfer',
        )}
      </DialogTitle>

      {data?.lastTransferDate && new Date(data?.lastTransferDate) < today && (
        <Alert severity="warning" sx={{ mb: 2, cursor: 'pointer' }}>
          {t('orderDetailPage.transferTourDialog.dateWarningText')}
        </Alert>
      )}
      {!(new Date(data?.lastTransferDate) < today) ||
        (!data?.lastTransferDate && (
          <DialogContent>
            {step === 1 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#fff9db',
                }}
              >
                <Typography component="ul" sx={{ pl: 2 }}>
                  <li>{t('orderDetailPage.transferTourDialog.readCode')}</li>
                  <li>{t('orderDetailPage.transferTourDialog.onlyValid')}</li>
                  <li>
                    {t('orderDetailPage.transferTourDialog.detailsChange')}
                  </li>
                  <li>{t('orderDetailPage.transferTourDialog.checkCode')}</li>
                </Typography>
              </Paper>
            ) : (
              <Box
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={2}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2,
                  }}
                >
                  <Typography>
                    <strong>
                      {t('orderDetailPage.transferTourDialog.orderNumber')}:
                    </strong>{' '}
                    {data.orderNumber}
                  </Typography>
                  <Typography>
                    <strong>
                      {t('orderDetailPage.transferTourDialog.pnr')}:
                    </strong>{' '}
                    {data.pnr}
                  </Typography>
                  <Typography>
                    <strong>
                      {t('orderDetailPage.transferTourDialog.currentTour')}:
                    </strong>{' '}
                    {data.tourInfo}
                  </Typography>
                  <Typography>
                    <strong>
                      {t('orderDetailPage.transferTourDialog.tourDate')}:
                    </strong>{' '}
                    {format(data.orderDate, 'dd.MM.yyyy')}
                  </Typography>
                  <Box mt={2} display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label={t(
                        'orderDetailPage.transferTourDialog.newTourCode',
                      )}
                      fullWidth
                      value={newTourCode}
                      onChange={(e) => {
                        setNewTourCode(e.target.value);
                        setTransferError('');
                        setTourPriceOptions([]);
                      }}
                      disabled={submitting}
                    />

                    <Button
                      variant="outlined"
                      onClick={fetchTourPrices}
                      disabled={submitting || !newTourCode.trim()}
                    >
                      {t('orderDetailPage.transferTourDialog.loadTour')}
                    </Button>
                    {priceError && tourPriceOptions.length < 1 && (
                      <Typography color="error">
                        {t('orderDetailPage.transferTourDialog.priceError')}
                      </Typography>
                    )}
                    {priceNotFoundError && tourPriceOptions.length < 1 && (
                      <Typography color="error">
                        {t('orderDetailPage.transferTourDialog.closed')}
                      </Typography>
                    )}

                    {tourPriceOptions && tourPriceOptions.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h5" gutterBottom>
                          {t(
                            'orderDetailPage.transferTourDialog.selectedPriceInfo',
                          )}
                        </Typography>
                        <Typography>
                          <strong>
                            {t('orderDetailPage.transferTourDialog.tourInfo')}:
                          </strong>{' '}
                          {tourPriceOptions[0]?.tour?.name}
                        </Typography>
                        <Typography>
                          <strong>
                            {t('orderDetailPage.transferTourDialog.bedType')}:
                          </strong>{' '}
                          {tourPriceOptions[0]?.price?.bedType.name} –{' '}
                          {tourPriceOptions[0]?.price?.concept.name} –{' '}
                          {tourPriceOptions[0]?.price?.bedStatus.name}
                        </Typography>
                        <Typography>
                          <strong>
                            {t('orderDetailPage.transferTourDialog.price')}:
                          </strong>{' '}
                          {tourPriceOptions[0]?.price?.amount}{' '}
                          {tourPriceOptions[0]?.price?.currency.code}
                        </Typography>
                        <Typography>
                          <strong>
                            {t('orderDetailPage.transferTourDialog.tourDate')}:
                          </strong>{' '}
                          {format(
                            tourPriceOptions[0]?.tour?.startDate,
                            'dd.MM.yyyy',
                          )}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}

            {step === 2 && transferError && (
              <Alert severity="error" sx={{ alignItems: 'center', mb: 1 }}>
                {transferError}
              </Alert>
            )}
          </DialogContent>
        ))}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCloseDialog}
          disabled={submitting}
        >
          {t('orderDetailPage.transferTourDialog.cancel')}
        </Button>
        {step === 1 ? (
          (!data?.lastTransferDate ||
            new Date(data.lastTransferDate) >= today) && (
            <Button variant="contained" onClick={handleNext}>
              {t('orderDetailPage.transferTourDialog.start')}
            </Button>
          )
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={tourPriceOptions.length < 1 || submitting}
          >
            {t('orderDetailPage.transferTourDialog.confirm')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
